import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import './App.css';
import './performance.css';
import SleekLoader from './components/Loader';
import SEO from './components/SEO';
import ErrorBoundary from './components/ErrorBoundary';
import Lenis from '@studio-freight/lenis';

// Lazy load pages for better performance
const Header = lazy(() => import('./components/Header'));
const LightThemeHero = lazy(() => import('./components/HeroSection'));
const ProductsShowcase = lazy(() => import('./components/ProductsShowcase'));
const BrandsEcosystem = lazy(() => import('./components/BrandsEcosystem'));
const TechnologySection = lazy(() => import('./components/TechnologySection'));
const IndustriesSection = lazy(() => import('./components/IndustriesSection'));
const ContactSection = lazy(() => import('./components/ContactSection'));
const Footer = lazy(() => import('./components/Footer'));
const CanoBondPage = lazy(() => import('./pages/CanoBondPage'));
const CanoBondPlywoodPage = lazy(() => import('./pages/CanoBondPlywoodPage'));
const BlackberryPage = lazy(() => import('./pages/BlackberryPage'));
const QuroTuffPage = lazy(() => import('./pages/QuroTuffPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const DistributorsPage = lazy(() => import('./pages/DistributorsPage'));
const SocialMediaFloat = lazy(() => import('./components/SocialMediaFloat'));
import { Toaster } from './components/ui/sonner';

// Admin imports (not lazy loaded for security)
import { AuthProvider } from './admin/context/AuthContext';
import ProtectedRoute from './admin/components/ProtectedRoute';
import AdminLogin from './admin/pages/AdminLogin';
import AdminLayout from './admin/components/AdminLayout';
import AdminDashboard from './admin/pages/AdminDashboard';
import ProductsManager from './admin/pages/ProductsManager';
import BrandsManager from './admin/pages/BrandsManager';
import CompanyManager from './admin/pages/CompanyManager';

function HomePage() {
  return (
    <>
      <Header />
      <main>
        <LightThemeHero />
        <ProductsShowcase />
        <BrandsEcosystem />
        <TechnologySection />
        <IndustriesSection />
        <ContactSection />
      </main>
      <Footer />
    </>
  );
}

function App() {
  const [loading, setLoading] = useState(true);

  const handleLoadingComplete = () => {
    setLoading(false);
  };

  // Optimized smooth scrolling for performance
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.0, // Reduced from 1.5s for better responsiveness
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // Exponential decay for smooth deceleration
      smoothWheel: true,
      wheelMultiplier: 1.0, // Increased from 0.8 for more responsive feel
      touchMultiplier: 2.0, // Native-like touch response
      smoothTouch: false, // Disable smooth scroll on touch devices - use native for better performance
      infinite: false,
      syncTouch: true, // Keep touch synchronized for better mobile experience
    });

    let rafId;
    function raf(time) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }

    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  // Smooth scroll behavior
  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <div className="App">
          {loading && <SleekLoader onComplete={handleLoadingComplete} />}
          
          {!loading && (
            <>
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="text-xl">Loading...</div></div>}>
                <Routes>
                {/* Public Routes */}
                <Route path="/" element={<HomePage />} />
                <Route path="/canobond" element={<CanoBondPage />} />
                <Route path="/canobond-plywood" element={<CanoBondPlywoodPage />} />
                <Route path="/blackberry" element={<BlackberryPage />} />
                <Route path="/qurotuff" element={<QuroTuffPage />} />
                <Route path="/furniture" element={<QuroTuffPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/distributors" element={<DistributorsPage />} />

                {/* Admin Routes */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="dashboard" element={<AdminDashboard />} />
                  <Route path="products" element={<ProductsManager />} />
                  <Route path="brands" element={<BrandsManager />} />
                  <Route path="company" element={<CompanyManager />} />
                </Route>
              </Routes>
              <SocialMediaFloat />
            </Suspense>
            <Toaster />
          </>
        )}
      </div>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
